import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { FaUser, FaEnvelope, FaPhone, FaMotorcycle, FaCheckCircle } from 'react-icons/fa'
import { Modal, InputGroup, Button } from '../../../../UI'
import { assignExistingRider, verifyRiderEmail } from '../../../../store/actions/riders'
import Container from './styles'

const AddExistingRiderModal = ({ show, handleClose }) => {
  const dispatch = useDispatch()
  
  // Form states
  const [email, setEmail] = useState('')
  const [step, setStep] = useState(1) // 1: Enter email, 2: Confirm details
  const [riderDetails, setRiderDetails] = useState(null)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const handleEmailChange = (e) => {
    setEmail(e.target.value)
    if (error) setError('')
  }

  const validateEmail = () => {
    if (!email?.trim()) {
      setError('Email is required')
      return false
    }
    
    if (!/\S+@\S+\.\S+/.test(email)) {
      setError('Please enter a valid email address')
      return false
    }

    return true
  }

  const handleVerifyEmail = async (e) => {
    e.preventDefault()

    if (!validateEmail()) return

    setLoading(true)

    const result = await verifyRiderEmail(email.trim())

    setLoading(false)

    if (result.success && result.rider) {
      setRiderDetails(result.rider)
      setStep(2)
      setError('')
    } else {
      setError(result.message || 'Rider not found. Please check the email address.')
    }
  }

  const handleConfirmAssignment = async () => {
    setLoading(true)

    const result = await dispatch(assignExistingRider(email.trim()))

    setLoading(false)

    if (result.success) {
      handleClose()
      resetForm()
    } else {
      setError(result.message || 'Failed to add rider. Please try again.')
    }
  }

  const handleBack = () => {
    setStep(1)
    setRiderDetails(null)
    setError('')
  }

  const resetForm = () => {
    setEmail('')
    setStep(1)
    setRiderDetails(null)
    setError('')
  }

  const onClose = () => {
    handleClose()
    resetForm()
  }

  return (
    <Modal showModal={show} onClose={onClose} showCloseBtn={true}>
      <Container>
        <div className="modal--container">
          <header>
            <h2>{step === 1 ? 'Add Existing Rider' : 'Confirm Rider Details'}</h2>
            <p>
              {step === 1 
                ? 'Enter the email address of a rider who already has an account' 
                : 'Review the rider information before adding to your company'}
            </p>
          </header>

          {step === 1 ? (
            <form onSubmit={handleVerifyEmail}>
              <div className="form-content">
                <div className="info-box">
                  <p>
                    Enter the email address of a rider who has already registered on the platform. 
                    We'll verify their account and show you their details.
                  </p>
                </div>

                <InputGroup
                  label="Rider Email Address"
                  name="email"
                  type="email"
                  value={email}
                  onChange={handleEmailChange}
                  placeholder="Enter rider's email address"
                  required
                />
                {error && (
                  <div className="error-message">{error}</div>
                )}

                <div className="button-row">
                  <Button type="button" onClick={onClose} className="btn-secondary">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify Email'}
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <div className="details-content">
              {riderDetails && (
                <>
                  <div className="rider-card">
                    <div className="rider-avatar">
                      <FaUser />
                    </div>
                    <div className="rider-info">
                      <h3>
                        {riderDetails.firstName} {riderDetails.lastName}
                      </h3>
                      
                      <div className="info-row">
                        <FaEnvelope className="icon" />
                        <span>{riderDetails.email}</span>
                      </div>

                      {riderDetails.phone && (
                        <div className="info-row">
                          <FaPhone className="icon" />
                          <span>{riderDetails.phone}</span>
                        </div>
                      )}

                      {riderDetails.vehicleType && (
                        <div className="info-row">
                          <FaMotorcycle className="icon" />
                          <span>
                            {riderDetails.vehicleType}
                            {riderDetails.vehicleNumber && ` - ${riderDetails.vehicleNumber}`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="confirmation-box">
                    <FaCheckCircle className="check-icon" />
                    <p>
                      This rider will be assigned to your company and can start accepting 
                      deliveries immediately.
                    </p>
                  </div>

                  {error && (
                    <div className="error-message">{error}</div>
                  )}

                  <div className="button-row">
                    <Button type="button" onClick={handleBack} className="btn-secondary">
                      Back
                    </Button>
                    <Button onClick={handleConfirmAssignment} disabled={loading}>
                      {loading ? 'Adding...' : 'Confirm & Add Rider'}
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Container>
    </Modal>
  )
}

export default AddExistingRiderModal
